import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/** One breadcrumb: a panel's name, and whether it is the panel Back returns to. */
interface Crumb {
  label: string;
  /** The panel directly underneath — clicking it is Back. */
  isParent: boolean;
  /** A run of panels left out of a long chain. */
  isGap: boolean;
}

/**
 * The strip a nested side panel shows above its own header (COL-973 drill-in): Back, and where you
 * are — `← MDU2 › WO-1042 › Action`. Drawn by SidePanelStackService into the child panel's overlay
 * pane, above the dialog itself, so no panel has to make room for it in its own template.
 *
 * A long chain keeps its first and last ancestors and folds the middle into "…".
 */
@Component({
  selector: 'app-side-panel-stack-bar',
  standalone: true,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="sps-bar" aria-label="Panels">
      <button type="button" class="sps-bar__back" data-testid="side-panel-back"
        [attr.aria-label]="'Back to ' + parentLabel()" [attr.title]="'Back to ' + parentLabel()" (click)="back.emit()">
        <mat-icon aria-hidden="true" svgIcon="c:arrow-left"></mat-icon>
      </button>
      <ol class="sps-bar__crumbs">
        @for (crumb of crumbs(); track $index) {
          <li class="sps-bar__crumb">
            @if (crumb.isParent) {
              <button type="button" class="sps-bar__link" [attr.title]="crumb.label" tabindex="-1" (click)="back.emit()">{{ crumb.label }}</button>
            } @else {
              <span class="sps-bar__text" [class.sps-bar__text--gap]="crumb.isGap" [attr.title]="crumb.isGap ? null : crumb.label">{{ crumb.label }}</span>
            }
            <span class="sps-bar__sep" aria-hidden="true">›</span>
          </li>
        }
        <li class="sps-bar__crumb sps-bar__crumb--current" aria-current="page">
          <span class="sps-bar__text" [attr.title]="current()">{{ current() }}</span>
        </li>
      </ol>
    </nav>
  `,
  styles: [`
    :host {
      display: block;
      flex: 0 0 auto;
      height: var(--side-panel-stack-bar-height, 44px);
      background: var(--white-color, #fff);
      border-bottom: 1px solid #e8eaed;
      font-family: var(--font-sans); font-weight: 400;
    }
    .sps-bar {
      display: flex;
      align-items: center;
      gap: 6px;
      height: 100%;
      padding: 0 16px 0 8px;
      min-width: 0;
    }
    .sps-bar__back {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      color: #4b5563;
      background: transparent;
      cursor: pointer;
    }
    .sps-bar__back:hover, .sps-bar__back:focus-visible {
      color: var(--color-text);
      background: #f3f4f6;
      outline: none;
    }
    .sps-bar__back:focus-visible { box-shadow: 0 0 0 2px var(--color-text); }
    .sps-bar__back mat-icon { width: 20px; height: 20px; font-size: 20px; }
    .sps-bar__crumbs {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      margin: 0;
      padding: 0;
      list-style: none;
      font-size: 13px;
      color: #6b7280;
    }
    .sps-bar__crumb {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      flex: 0 1 auto;
    }
    .sps-bar__crumb--current { flex: 0 0 auto; max-width: 45%; color: #111827; font-weight: 600; }
    .sps-bar__link, .sps-bar__text {
      overflow: hidden;
      max-width: 180px;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .sps-bar__crumb--current .sps-bar__text { max-width: 100%; }
    .sps-bar__link {
      padding: 0;
      border: 0;
      font: inherit;
      color: var(--color-text);
      background: none;
      cursor: pointer;
    }
    .sps-bar__link:hover { text-decoration: underline; }
    .sps-bar__text--gap { flex: 0 0 auto; }
    .sps-bar__sep { flex: 0 0 auto; color: #9ca3af; }
  `],
})
export class SidePanelStackBarComponent {
  readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The panels underneath, root first — their titles. */
  readonly ancestors = input<string[]>([]);
  /** What the panel showing is (Inspection, Action, …). */
  readonly current = input('');
  /** Back: return to the panel underneath. */
  readonly back = output<void>();

  readonly parentLabel = computed(() => this.ancestors().at(-1) ?? 'previous panel');

  readonly crumbs = computed<Crumb[]>(() => {
    const labels = this.ancestors();
    const last = labels.length - 1;
    const all = labels.map((label, i) => ({ label, isParent: i === last, isGap: false }));
    // Root, …, parent: the chain's two ends say where it started and where Back goes.
    if (all.length <= 2) return all;
    return [all[0], { label: '…', isParent: false, isGap: true }, all[last]];
  });

  /** Focus Back — the child panel's first stop, as the panel it replaced is no longer there. */
  focusBack(): void {
    this.host.nativeElement.querySelector<HTMLElement>('.sps-bar__back')?.focus({ preventScroll: true });
  }
}
